import {
  Badge,
  Box,
  CellProps,
  Column,
  ConnectedIcon,
  DisconnectedIcon,
  PaginationHook,
  TableColumnSortOrder,
  TableWithPagination,
  useSelectColumn,
  useTableHeadCell,
} from "@shared/primitive-components"
import { useCallback, useMemo } from "react"
import { LegacyFleetEntry } from "@shared/utils/compatibility"
import { buildMaybeObfuscatedHeader } from "../../../components/Obfuscated"
import { FleetListData } from "../fleetListHandlers"
import { TEST_IDS } from "./fleetListTestIds"
import * as styles from "./styles/fleetListTableStyles"
import { FleetEntry } from "../../../api/fleet"

const StatusCell = ({ value }: CellProps<string, FleetListData>) => (
  <>
    {value === `scheduled` ? (
      <Badge variant="secondary">scheduled</Badge>
    ) : value === `off_cover` ? (
      <Badge variant="default">off cover</Badge>
    ) : (
      <Badge variant="brand">on cover</Badge>
    )}
  </>
)
const ConnectedCell = ({ value }: CellProps<boolean, FleetListData>) => (
  <Box>{value ? <ConnectedIcon /> : <DisconnectedIcon />}</Box>
)

type FleetListTableProps = Readonly<{
  page: ReadonlyArray<FleetListData>
  selectedRows: ReadonlyArray<FleetListData>
  dataCount: number
  enableSelectionColumn: boolean
  pagination: PaginationHook
  sortData: (property: keyof LegacyFleetEntry, newSort: TableColumnSortOrder | undefined) => void
  onSetSelectedRowsChanged: (selectedRows: ReadonlyArray<FleetListData>) => void
  excludedColumns?: Array<keyof LegacyFleetEntry>
  loading?: boolean
}>

export const FleetListTable = ({
  page,
  selectedRows,
  dataCount,
  pagination,
  sortData,
  enableSelectionColumn,
  onSetSelectedRowsChanged,
  excludedColumns = [],
  loading,
}: FleetListTableProps) => {
  const { selectColumn, selectRow } = useSelectColumn<FleetListData>(
    page,
    `id`,
    selectedRows,
    onSetSelectedRowsChanged,
    {
      selectAll: TEST_IDS.SELECT_ALL,
      selectSingle: TEST_IDS.SELECT_SINGLE,
    }
  )

  const { HeaderCell } = useTableHeadCell(sortData, [], dataCount > 0)

  const MakeHeader = useCallback(() => <HeaderCell header="Make" property="make" />, [HeaderCell])
  const ModelHeader = useCallback(() => <HeaderCell header="Model" property="model" />, [HeaderCell])
  const RegistrationHeader = useCallback(
    () => <HeaderCell header="Registration" property="registrationNumber" />,
    [HeaderCell]
  )
  const AbiHeader = useCallback(() => <HeaderCell header="ABI" property="abiGroup" />, [HeaderCell])
  const OnCoverHeader = useCallback(() => <HeaderCell header="On cover from" property="onCoverDate" />, [HeaderCell])
  const OffCoverHeader = useCallback(() => <HeaderCell header="On cover until" property="offCoverDate" />, [HeaderCell])
  const SentToMidHeader = useCallback(() => <HeaderCell header="Sent to MID" property="sentToMID" />, [HeaderCell])
  const StatusHeader = useCallback(() => <HeaderCell header="Status" property="status" />, [HeaderCell])
  const ConnectedHeader = useCallback(() => <HeaderCell header="Connected" property="isConnected" />, [HeaderCell])

  const columns = useMemo<Array<Column<FleetListData>>>(() => {
    const tableColumns: Array<Column<FleetListData>> = [
      {
        Header: MakeHeader,
        accessor: `make`,
      },
      {
        Header: ModelHeader,
        accessor: `model`,
      },
      buildMaybeObfuscatedHeader<FleetListData>(`registrationNumber`, RegistrationHeader, { type: `vrn` }),
      {
        Header: AbiHeader,
        accessor: `abiGroupString`,
      },
      {
        Header: OnCoverHeader,
        accessor: `onCoverDate`,
      },
      {
        Header: OffCoverHeader,
        accessor: `offCoverDate`,
      },
      {
        Header: SentToMidHeader,
        accessor: `sentToMID`,
      },
      {
        Header: StatusHeader,
        accessor: `status`,
        Cell: StatusCell,
      },
      {
        Header: ConnectedHeader,
        accessor: `isConnected`,
        Cell: ConnectedCell,
      },
    ]
    if (enableSelectionColumn) {
      tableColumns.unshift(selectColumn)
    }

    if (excludedColumns) {
      return tableColumns.filter(
        ({ accessor }) => accessor !== undefined && !excludedColumns.includes(`${accessor}` as keyof FleetEntry)
      )
    }

    return tableColumns
  }, [
    AbiHeader,
    ConnectedHeader,
    excludedColumns,
    enableSelectionColumn,
    MakeHeader,
    ModelHeader,
    OffCoverHeader,
    OnCoverHeader,
    RegistrationHeader,
    selectColumn,
    SentToMidHeader,
    StatusHeader,
  ])

  return (
    <TableWithPagination<FleetListData>
      data={page}
      dataCount={dataCount}
      pagination={pagination}
      columns={columns}
      rowOnClickValueKey="id"
      rowOnClick={selectRow}
      loading={loading}
      css={{ row: styles.highlightRow }}
      selectedRows={selectedRows}
    />
  )
}
